import React from 'react'
import { KeyIcon, PlusIcon, ClipboardDocumentIcon, UserIcon } from '@heroicons/react/24/outline'

const ApiKeysSection = ({
  allApiKeys,
  filteredAndSortedApiKeys,
  showRevokedKeys,
  setShowRevokedKeys,
  handleCreateApiKey,
  handleRevokeApiKey,
  createApiKeyMutation,
  revokingKeyId,
  userInfo,
  copyToClipboard
}) => {
  return (
    <div className="bg-white rounded-xl shadow-sm border border-gray-200 mb-8">
      <div className="flex justify-between items-center px-6 py-4 border-b border-gray-200">
        <div>
          <h3 className="text-lg font-semibold text-gray-900">API Authentication</h3>
          <p className="text-sm text-gray-600 mt-1">Manage your API keys and user identification</p>
        </div>
        {allApiKeys.length > 0 && (
          <button
            onClick={handleCreateApiKey}
            disabled={createApiKeyMutation.isPending}
            className="bg-blue-600 text-white px-4 py-2 rounded-lg hover:bg-blue-700 transition-colors flex items-center text-sm font-medium disabled:opacity-50 disabled:cursor-not-allowed"
          >
            <PlusIcon className="h-4 w-4 mr-2" />
            {createApiKeyMutation.isPending ? 'Creating...' : 'Create New Key'}
          </button>
        )}
      </div>
      <div className="p-6">
        {/* User ID Section */}
        {userInfo?.id && (
          <div className="mb-6">
            <div className="bg-blue-50 rounded-lg p-4 border border-blue-200">
              <div className="flex items-center justify-between">
                <div className="flex items-center space-x-3">
                  <div className="p-2 bg-blue-100 rounded-lg">
                    <UserIcon className="h-4 w-4 text-blue-600" />
                  </div>
                  <div>
                    <h4 className="text-sm font-medium text-gray-900">Your User ID</h4>
                    <p className="text-xs text-gray-600">
                      Use this ID in your API requests for user identification
                    </p>
                  </div>
                </div>
                <div className="flex items-center space-x-2">
                  <code className="text-sm font-mono text-gray-800 bg-white px-3 py-1 rounded border">
                    {userInfo.id}
                  </code>
                  <button
                    onClick={() => copyToClipboard(userInfo.id, 'User ID')}
                    className="p-2 text-blue-600 hover:text-blue-800 hover:bg-blue-100 rounded-lg transition-colors"
                    title="Copy User ID"
                  >
                    <ClipboardDocumentIcon className="h-4 w-4" />
                  </button>
                </div>
              </div>
            </div>
          </div>
        )}

        {/* Show revoked keys toggle */}
        {allApiKeys.some(key => key.status === 'revoked') && (
          <div className="mb-6">
            <div className="bg-gray-50 rounded-lg p-4 border border-gray-200">
              <div className="flex items-center justify-between">
                <div className="flex items-center space-x-3">
                  <div className="p-2 bg-red-100 rounded-lg">
                    <svg className="h-4 w-4 text-red-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-2.5L13.732 4c-.77-.833-1.964-.833-2.732 0L4.082 16.5c-.77.833.192 2.5 1.732 2.5z" />
                    </svg>
                  </div>
                  <div>
                    <h4 className="text-sm font-medium text-gray-900">Revoked Keys</h4>
                    <p className="text-xs text-gray-600">
                      {allApiKeys.filter(key => key.status === 'revoked').length} revoked key{allApiKeys.filter(key => key.status === 'revoked').length !== 1 ? 's' : ''} available
                    </p>
                  </div>
                </div>
                <label className="relative inline-flex items-center cursor-pointer">
                  <input
                    type="checkbox"
                    checked={showRevokedKeys}
                    onChange={(e) => setShowRevokedKeys(e.target.checked)}
                    className="sr-only peer"
                  />
                  <div className="w-12 h-6 bg-gray-200 peer-focus:outline-none peer-focus:ring-4 peer-focus:ring-blue-100 rounded-full peer peer-checked:after:translate-x-6 peer-checked:after:border-white after:content-[''] after:absolute after:top-0.5 after:left-[2px] after:bg-white after:rounded-full after:h-5 after:w-5 after:transition-all peer-checked:bg-blue-600 shadow-inner">
                  </div>
                  <span className="ml-3 text-sm font-medium text-gray-700">
                    {showRevokedKeys ? 'Hide' : 'Show'}
                  </span>
                </label>
              </div>
            </div>
          </div>
        )}

        {filteredAndSortedApiKeys.length === 0 ? (
          <div className="text-center py-12">
            <KeyIcon className="h-12 w-12 mx-auto text-gray-300 mb-4" />
            {allApiKeys.length === 0 ? (
              <>
                <h4 className="text-lg font-medium text-gray-900 mb-2">No API keys yet</h4>
                <p className="text-gray-600 mb-6">Create your first API key to start using the EasyAppIcon API programmatically.</p>
                <button
                  onClick={handleCreateApiKey}
                  disabled={createApiKeyMutation.isPending}
                  className="bg-blue-600 text-white px-6 py-3 rounded-lg hover:bg-blue-700 transition-colors flex items-center mx-auto font-medium disabled:opacity-50 disabled:cursor-not-allowed"
                >
                  <PlusIcon className="h-5 w-5 mr-2" />
                  {createApiKeyMutation.isPending ? 'Creating...' : 'Create Your First API Key'}
                </button>
              </>
            ) : (
              <>
                <h4 className="text-lg font-medium text-gray-900 mb-2">No active API keys</h4>
                <p className="text-gray-600 mb-6">
                  {showRevokedKeys
                    ? "All your API keys have been revoked."
                    : "All your active keys are hidden. Toggle 'Show revoked keys' above to view revoked keys."
                  }
                </p>
                <button
                  onClick={handleCreateApiKey}
                  disabled={createApiKeyMutation.isPending}
                  className="bg-blue-600 text-white px-6 py-3 rounded-lg hover:bg-blue-700 transition-colors flex items-center mx-auto font-medium disabled:opacity-50 disabled:cursor-not-allowed"
                >
                  <PlusIcon className="h-5 w-5 mr-2" />
                  {createApiKeyMutation.isPending ? 'Creating...' : 'Create New API Key'}
                </button>
              </>
            )}
          </div>
        ) : (
          <div className="space-y-4">
            {filteredAndSortedApiKeys.map((apiKey, index) => {
              const keyId = apiKey.id || apiKey.maskedKey || index
              return (
                <div key={keyId} className="bg-gray-50 rounded-lg p-4 border border-gray-200">
                  <div className="flex items-center justify-between">
                    <div className="flex-1">
                      <div className="flex items-center space-x-3 mb-3">
                        <h4 className="text-sm font-semibold text-gray-900">
                          {apiKey.keyName}
                        </h4>
                        <span className={`px-2 py-1 rounded-full text-xs font-medium ${
                          apiKey.status === 'active'
                            ? 'bg-green-100 text-green-800'
                            : 'bg-red-100 text-red-800'
                        }`}>
                          {apiKey.status}
                        </span>
                      </div>

                      <div className="flex items-center space-x-4 text-xs text-gray-500 mb-3">
                        <span>Created: {new Date(apiKey.createdAt).toLocaleDateString()}</span>
                        {apiKey.lastUsedAt && (
                          <span>Last used: {new Date(apiKey.lastUsedAt).toLocaleDateString()}</span>
                        )}
                        <span>Usage: {apiKey.usageCount || 0}</span>
                      </div>
                    </div>

                    <div className="flex items-center space-x-2 ml-4">
                      <button
                        onClick={() => handleRevokeApiKey(apiKey.keyId || apiKey.id || apiKey.maskedKey, apiKey.keyName)}
                        disabled={revokingKeyId === (apiKey.keyId || apiKey.id || apiKey.maskedKey) || apiKey.status === 'revoked'}
                        className="text-red-600 hover:text-red-800 text-sm font-medium px-3 py-1 rounded hover:bg-red-50 transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
                      >
                        {revokingKeyId === (apiKey.keyId || apiKey.id || apiKey.maskedKey) ? 'Revoking...' : 'Revoke'}
                      </button>
                    </div>
                  </div>
                </div>
              )
            })}
          </div>
        )}
      </div>
    </div>
  )
}

export default ApiKeysSection
